import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Plus, FileDown, ShieldCheck, AlertTriangle, Info, Lock, CheckCircle2, XCircle, Edit3, Save, X, ChevronRight, History, Zap, ListOrdered, Trash2, Pencil, Share2, Copy, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";
import { SearchableSelect } from "./SearchableSelect";
import ModalPortal from "@/components/ui/modal-portal";
import FieldEventForm, { FIELD_EVENT_TYPES, isFieldEvent } from "./FieldEventForm";

const USAGE_TYPES = [
  { value: "TRAITEMENT_SURFACE", label: "Traitement sur parcelle / surface", icon: "🌾" },
  { value: "ESPACE_FERME", label: "Traitement en espace fermé", icon: "🏭" },
  { value: "TRAITEMENT_SEMENCES_PLANTS", label: "Traitement de semences ou plants", icon: "🌱" },
  { value: "SEMIS_SEMENCES_TRAITEES", label: "Semis de semences traitées", icon: "🚜" },
];

const STATUS_META = {
  draft: { label: "Brouillon", bg: "#E5E7EB", color: "#374151", icon: Edit3 },
  validated: { label: "Validée", bg: "#DBEAFE", color: "#1E40AF", icon: CheckCircle2 },
  locked: { label: "Verrouillée", bg: "#D1FAE5", color: "#065F46", icon: Lock },
  corrected: { label: "Corrigée", bg: "#FEF3C7", color: "#92400E", icon: History },
  exported: { label: "Exportée", bg: "#EDE9FE", color: "#5B21B6", icon: FileDown },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: m.bg, color: m.color }}>
      <Icon className="w-2.5 h-2.5" />
      {m.label}
    </span>
  );
}

// ==================== PARCEL REGISTRY INFO PANEL ====================
function ParcelRegistryInfoPanel({ parcelId, onSaved }) {
  const [info, setInfo] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchInfo = useCallback(async () => {
    const res = await authFetch(`${API_BASE}/phyto-register/parcels/${parcelId}/registry-info`);
    if (res.ok) setInfo(await res.json());
  }, [parcelId]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const save = async () => {
    setSaving(true);
    const res = await authFetch(`${API_BASE}/phyto-register/parcels/${parcelId}/registry-info`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        siret_holder: info.siret_holder,
        business_name: info.business_name,
        pacage: info.pacage,
        organic_production: info.organic_production,
        beneficiary_siret: info.beneficiary_siret,
        beneficiary_name: info.beneficiary_name,
        is_service_provider_case: info.is_service_provider_case,
      }),
    });
    setSaving(false);
    if (res.ok) {
      toast.success("Informations réglementaires enregistrées");
      setEditing(false);
      onSaved?.();
    } else {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  if (!info) return null;

  const filled = !!(info.siret_holder && info.siret_holder.length === 14);

  return (
    <div className="mb-4 p-3 rounded-lg border" style={{ borderColor: filled ? "#10B98140" : "#F59E0B40", background: filled ? "#ECFDF5" : "#FFFBEB" }} data-testid="registre-parcel-info-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" style={{ color: filled ? "#059669" : "#D97706" }} />
          <span className="text-xs font-bold" style={{ color: filled ? "#065F46" : "#92400E" }}>
            {filled ? "Exploitation détentrice configurée" : "Configurez le SIRET détenteur pour cette parcelle"}
          </span>
          {filled && info.source === "convention" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-800" data-testid="registry-from-convention">
              repris de la convention du titulaire
            </span>
          )}
        </div>
        {!editing && (
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setEditing(true)} data-testid="edit-parcel-registry-btn">
            <Edit3 className="w-3 h-3 mr-1" /> Modifier
          </Button>
        )}
      </div>

      {editing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase font-semibold text-gray-600">SIRET détenteur * (14 chiffres)</Label>
            <Input value={info.siret_holder || ""} onChange={(e) => setInfo({ ...info, siret_holder: e.target.value.replace(/\D/g, "").slice(0, 14) })} placeholder="12345678901234" className="h-8 text-sm mt-1" data-testid="input-siret-holder" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-semibold text-gray-600">Raison sociale</Label>
            <Input value={info.business_name || ""} onChange={(e) => setInfo({ ...info, business_name: e.target.value })} placeholder="Nom de l'exploitation" className="h-8 text-sm mt-1" data-testid="input-business-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-semibold text-gray-600">N° PACAGE (facultatif)</Label>
            <Input value={info.pacage || ""} onChange={(e) => setInfo({ ...info, pacage: e.target.value })} placeholder="974xxxxxx" className="h-8 text-sm mt-1" data-testid="input-pacage" />
          </div>
          <div className="flex items-center gap-2 pt-4">
            <Switch checked={!!info.organic_production} onCheckedChange={(v) => setInfo({ ...info, organic_production: v })} data-testid="switch-organic" />
            <Label className="text-xs">Production biologique (AB)</Label>
          </div>
          <div className="md:col-span-2 border-t pt-2 mt-1">
            <div className="flex items-center gap-2 mb-2">
              <Switch checked={!!info.is_service_provider_case} onCheckedChange={(v) => setInfo({ ...info, is_service_provider_case: v })} data-testid="switch-service-provider" />
              <Label className="text-xs">Cas prestation / entraide (SIRET bénéficiaire différent)</Label>
            </div>
            {info.is_service_provider_case && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input value={info.beneficiary_siret || ""} onChange={(e) => setInfo({ ...info, beneficiary_siret: e.target.value.replace(/\D/g, "").slice(0, 14) })} placeholder="SIRET bénéficiaire (14 ch.)" className="h-8 text-sm" data-testid="input-siret-benef" />
                <Input value={info.beneficiary_name || ""} onChange={(e) => setInfo({ ...info, beneficiary_name: e.target.value })} placeholder="Nom du bénéficiaire" className="h-8 text-sm" data-testid="input-name-benef" />
              </div>
            )}
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 mt-2">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); fetchInfo(); }} className="h-8 text-xs"><X className="w-3 h-3 mr-1" />Annuler</Button>
            <Button size="sm" onClick={save} disabled={saving} className="h-8 text-xs bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-parcel-registry-btn">
              <Save className="w-3 h-3 mr-1" />Enregistrer
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
          <div><span className="text-gray-500">SIRET :</span> <span className="font-mono">{info.siret_holder || "—"}</span></div>
          <div><span className="text-gray-500">Raison sociale :</span> {info.business_name || "—"}</div>
          <div><span className="text-gray-500">PACAGE :</span> {info.pacage || "—"}</div>
          <div><span className="text-gray-500">Bio :</span> {info.organic_production ? "Oui" : (info.organic_production === false ? "Non" : "—")}</div>
        </div>
      )}
    </div>
  );
}

/** Dose saisie comparée aux règles E-Phy du produit pour la culture : dose maximale,
 *  alertes liées aux autres applications sur la parcelle, conditions d'emploi officielles. */
function DoseLimitNotice({ check, crop }) {
  if (!check) return null;
  const history = check.history || [];
  const conditions = check.conditions || [];
  return (
    <div className="space-y-1.5">
      {check.status === "above" && (
        <div className="rounded-md border border-red-300 bg-red-50 px-2.5 py-1.5 text-[11px] text-red-800" data-testid="dose-above-max">
          <b>Dose supérieure au maximum autorisé</b> pour « {check.usage} » : {check.max_text} maximum (E-Phy). Un dépassement de dose homologuée est interdit.
        </div>
      )}
      {check.status === "no_usage" && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800" data-testid="dose-no-usage">
          Aucune dose E-Phy de ce produit pour « {crop} ». Vérifiez qu'il est autorisé sur cette culture.
        </div>
      )}
      {history.map((h) => (
        <div key={h.code} className="rounded-md border border-red-300 bg-red-50 px-2.5 py-1.5 text-[11px] text-red-800" data-testid={`dose-history-${h.code}`}>
          {h.message}
        </div>
      ))}
      {check.status === "ok" && check.max_text && !history.length && (
        <p className="text-[10.5px] text-gray-500" data-testid="dose-max-info">Dose max. E-Phy pour « {check.usage} » : {check.max_text}</p>
      )}
      {conditions.length > 0 && (
        <details className="rounded-md border border-black/10 bg-gray-50 px-2.5 py-1.5 text-[11px]" data-testid="dose-conditions">
          <summary className="cursor-pointer font-semibold text-gray-700">
            Conditions d'emploi E-Phy ({conditions.length} usage{conditions.length > 1 ? "s" : ""}{check.target_matched ? " pour cette cible" : ""})
          </summary>
          <ul className="mt-1.5 space-y-1.5">
            {conditions.map((c, k) => (
              <li key={k} className="text-gray-700">
                <b>{c.cible || "Usage"}</b> · {c.dose}
                {c.applications_max ? ` · ${c.applications_max} appl. max` : ""}
                {c.intervalle_j ? ` · intervalle ${c.intervalle_j} j` : ""}
                {c.dar_j != null ? ` · DAR ${c.dar_j} j` : ""}
                {c.condition && <span className="block text-gray-500">{c.condition}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ==================== QUANTITÉ DE PRODUIT ====================
// Quantité = dose/ha × surface traitée, ou dose/hL (ou %) × volume de bouillie (L/ha) ÷ 100 × surface.
// Affichée dans une unité lisible (moins de 1 L → mL, moins de 1 kg → g) sans arrondi destructeur.
const PER_HL_UNITS = ["L/hL", "kg/hL", "%"];

export function computeQuantity(doseValue, doseUnit, surface, water) {
  const dose = parseFloat(doseValue), surf = parseFloat(surface);
  if (!Number.isFinite(dose) || !Number.isFinite(surf) || surf <= 0) return null;
  const unit = doseUnit || "L/ha";
  let amount, base;
  if (PER_HL_UNITS.includes(unit)) {
    const w = parseFloat(water);
    if (!Number.isFinite(w) || w <= 0) return null; // volume de bouillie nécessaire
    base = unit === "kg/hL" ? "kg" : "L";
    amount = dose * w / 100 * surf;
  } else {
    const [b, per] = unit.split("/");
    if (per !== "ha" || !["L", "mL", "kg", "g"].includes(b)) return null;
    base = b;
    amount = dose * surf;
  }
  const smaller = { L: "mL", kg: "g" }, bigger = { mL: "L", g: "kg" };
  if (smaller[base] && amount > 0 && amount < 1) { amount *= 1000; base = smaller[base]; }
  else if (bigger[base] && amount >= 1000) { amount /= 1000; base = bigger[base]; }
  const digits = amount < 10 ? 2 : amount < 100 ? 1 : 0;
  return { product_quantity_value: Math.round(amount * 10 ** digits) / 10 ** digits, product_quantity_unit: base };
}

// Quantité enregistrée avant cette version (arrondi à 0,01 dans l'unité de la dose)
function legacyQuantity(doseValue, doseUnit, surface) {
  if (doseValue == null || !surface) return null;
  const base = (doseUnit || "L/ha").split("/")[0];
  return { product_quantity_value: Math.round(doseValue * surface * 100) / 100, product_quantity_unit: ["L", "mL", "kg", "g"].includes(base) ? base : "L" };
}

const sameQuantity = (a, b) => !!a && !!b && a.product_quantity_value === b.product_quantity_value
  && (a.product_quantity_unit || "") === (b.product_quantity_unit || "");

// Une quantité déjà enregistrée suit-elle la dose (calculée) ou a-t-elle été saisie à la main ?
function quantityIsManual(quantity, doseValue, doseUnit, surface, water) {
  const q = quantity || {};
  if (q.quantity_manual != null) return !!q.quantity_manual;
  if (q.product_quantity_value == null) return false;
  return !(sameQuantity(q, legacyQuantity(doseValue, doseUnit, surface)) || sameQuantity(q, computeQuantity(doseValue, doseUnit, surface, water)));
}

const fmtQty = (q) => q ? `${q.product_quantity_value.toLocaleString("fr-FR")} ${q.product_quantity_unit}` : "";

/** Aide sous la case quantité : origine du calcul, ou proposition de recalcul. */
function QuantityHint({ computed, quantity, manual, needsWater, onRecalc, testId }) {
  if (!computed) {
    return needsWater ? <p className="text-[10.5px] text-amber-700" data-testid={testId}>Renseignez le volume de bouillie pour calculer la quantité</p> : null;
  }
  if (!manual && sameQuantity(quantity, computed)) {
    return <p className="text-[10.5px] text-emerald-700" data-testid={testId}>Calculée automatiquement</p>;
  }
  return (
    <button type="button" onClick={onRecalc} className="text-[10.5px] font-semibold text-emerald-700 underline text-left" data-testid={testId}>
      Recalculer : {fmtQty(computed)}
    </button>
  );
}

// ==================== WIZARD SAISIE EXPRESS (3 ÉTAPES) ====================
function RegistreWizard({ parcelId, ilotId, gpsOverride, underGreenhouse, installationLabel, parcelRegInfo, surfaceHa, cultureType, refData, onClose, onCreated, prefill, initialStep, editEntryId, correctingEntryId }) {
  const [step, setStep] = useState(initialStep || 1);
  const [productCategory, setProductCategory] = useState(prefill?.product?.category || "herbicide");
  const [frequentProducts, setFrequentProducts] = useState([]);
  const [weatherFailed, setWeatherFailed] = useState(false);
  const [data, setData] = useState(() => {
    const base = {
      parcel_id: parcelId,
      ilot_id: ilotId || null,
      usage_type: underGreenhouse ? "ESPACE_FERME" : "TRAITEMENT_SURFACE",
      campaign_year: new Date().getFullYear(),
      holding: {
        siret_holder: parcelRegInfo?.siret_holder || "",
        business_name: parcelRegInfo?.business_name || "",
        pacage: parcelRegInfo?.pacage || "",
      },
      beneficiary: {
        is_service_provider_case: parcelRegInfo?.is_service_provider_case || false,
        siret_beneficiary: parcelRegInfo?.beneficiary_siret || "",
        beneficiary_name: parcelRegInfo?.beneficiary_name || "",
      },
      product: {},
      timing: {
        timezone: (parcelId || "").startsWith("YT-") ? "Indian/Mayotte" : "Indian/Reunion",
        use_date: new Date().toISOString().slice(0, 10), // aujourd'hui par défaut
      },
      dose: {},
      quantity: {},
      treated_unit: surfaceHa ? { value: surfaceHa, unit: "ha" } : {},
      crop: {
        crop_text: cultureType || "",
        organic_production: parcelRegInfo?.organic_production ?? null,
      },
      location: {
        location_type: "GPS",
        gps_lat: gpsOverride?.lat ?? parcelRegInfo?.centroid_lat ?? null,
        gps_lon: gpsOverride?.lon ?? parcelRegInfo?.centroid_lon ?? null,
        insee_commune: parcelRegInfo?.insee_commune || "",
        parcel_name: parcelRegInfo?.parcel_id || "",
      },
      closed_space: {},
      seed_treatment: {},
      treated_seed_sowing: {},
      worker_reentry: { early_reentry: false },
      // Applicateur proposé : le signataire de la convention du titulaire (modifiable)
      recommended_extra_data: parcelRegInfo?.operator_default ? { operator_name: parcelRegInfo.operator_default } : {},
    };
    if (!prefill) return base;
    const { extra_products: _extras, ...rest } = prefill;
    const merged = { ...base, ...rest };
    merged.quantity = {
      ...(merged.quantity || {}),
      quantity_manual: quantityIsManual(merged.quantity, merged.dose?.value, merged.dose?.unit,
        merged.treated_unit?.value ?? surfaceHa, merged.recommended_extra_data?.water_volume_value),
    };
    return merged;
  });

  // Espace fermé : pré-remplit l'installation avec la position et la surface déjà connues
  useEffect(() => {
    if (data.usage_type !== "ESPACE_FERME") return;
    setData(d => {
      const cs = { ...(d.closed_space || {}) };
      let changed = false;
      if (!cs.installation_number && !cs.installation_name && installationLabel) {
        cs.installation_name = installationLabel;
        changed = true;
      }
      if (cs.installation_gps_lat == null && d.location?.gps_lat != null) {
        cs.installation_gps_lat = d.location.gps_lat;
        cs.installation_gps_lon = d.location.gps_lon;
        changed = true;
      }
      if ((cs.surface_or_volume_value == null || cs.surface_or_volume_value === "") && d.treated_unit?.value) {
        cs.surface_or_volume_value = d.treated_unit.value;
        cs.surface_or_volume_unit = cs.surface_or_volume_unit || d.treated_unit.unit || "ha";
        changed = true;
      }
      return changed ? { ...d, closed_space: cs } : d;
    });
  }, [data.usage_type]);

  // Produits fréquents de l'utilisateur (saisie express)
  useEffect(() => {
    authFetch(`${API_BASE}/phyto-register/frequent-products`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => setFrequentProducts(Array.isArray(list) ? list : []))
      .catch(() => {});
  }, []);
  const [compliance, setCompliance] = useState(null);
  const [saving, setSaving] = useState(false);
  const [correctionReason, setCorrectionReason] = useState("");
  // Mélange en cuve : produits additionnels du même passage (une entrée de registre par produit)
  const [extraProducts, setExtraProducts] = useState(() => (prefill?.extra_products || []).map(x => ({
    ...x,
    quantity: {
      ...(x.quantity || {}),
      quantity_manual: quantityIsManual(x.quantity, x.dose?.value, x.dose?.unit,
        prefill?.treated_unit?.value ?? surfaceHa, prefill?.recommended_extra_data?.water_volume_value),
    },
  })));
  const [mixCompat, setMixCompat] = useState(null);
  // Contrôle en direct des doses maximales (produit principal + produits du mélange)
  const [doseChecks, setDoseChecks] = useState([]);
  const [mixCompatLoading, setMixCompatLoading] = useState(false);
  const MAX_MIX = 4;

  const cropForDose = data.crop?.crop_text || cultureType || "";
  const doseCheckKey = JSON.stringify([
    cropForDose, data.recommended_extra_data?.water_volume_value ?? null,
    data.recommended_extra_data?.target || "", data.timing?.use_date || "",
    [data.product, ...extraProducts.map(x => x.product)].map(p => p?.amm_number || ""),
    [data.dose, ...extraProducts.map(x => x.dose)].map(d => [d?.value ?? null, d?.unit || ""]),
  ]);
  useEffect(() => {
    const items = [{ product: data.product, dose: data.dose }, ...extraProducts.map(x => ({ product: x.product, dose: x.dose }))];
    if (!cropForDose || !items.some(it => it.product?.amm_number)) { setDoseChecks([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const results = await Promise.all(items.map(it => (it.product?.amm_number
        ? authFetch(`${API_BASE}/phyto-register/dose-check`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              product: it.product, crop_text: cropForDose, dose: it.dose,
              water_volume_value: data.recommended_extra_data?.water_volume_value ?? null,
              target: data.recommended_extra_data?.target || null,
              parcel_id: parcelId, ilot_id: ilotId || null, use_date: data.timing?.use_date || null,
              entry_id: editEntryId || correctingEntryId || null,
            }),
          }).then(r => (r.ok ? r.json() : null)).catch(() => null)
        : Promise.resolve(null))));
      if (!cancelled) setDoseChecks(results);
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doseCheckKey]);

  // Quantité de produit synchronisée avec la dose, la surface traitée et le volume de bouillie,
  // tant qu'elle n'a pas été saisie à la main. À l'ouverture d'un brouillon, rien n'est modifié.
  const qtySurface = data.treated_unit?.value ?? surfaceHa;
  const qtyWater = data.recommended_extra_data?.water_volume_value;
  const quantityFor = (dose) => computeQuantity(dose?.value, dose?.unit, qtySurface, qtyWater);
  const qtyMounted = useRef(false);
  useEffect(() => {
    if (!qtyMounted.current) return;
    setData(d => {
      if (d.quantity?.quantity_manual) return d;
      const q = computeQuantity(d.dose?.value, d.dose?.unit, d.treated_unit?.value ?? surfaceHa, d.recommended_extra_data?.water_volume_value);
      const next = q || { product_quantity_value: null, product_quantity_unit: d.quantity?.product_quantity_unit };
      if (sameQuantity(d.quantity, next) || (!q && d.quantity?.product_quantity_value == null)) return d;
      return { ...d, quantity: { ...(d.quantity || {}), ...next, quantity_manual: false } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.dose?.value, data.dose?.unit, qtySurface, qtyWater]);
  useEffect(() => {
    if (!qtyMounted.current) return;
    setExtraProducts(prev => prev.map(x => {
      if (x.quantity?.quantity_manual) return x;
      const q = quantityFor(x.dose);
      return q && !sameQuantity(x.quantity, q) ? { ...x, quantity: { ...(x.quantity || {}), ...q, quantity_manual: false } } : x;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtySurface, qtyWater]);
  useEffect(() => { qtyMounted.current = true; }, []);
  const recalcQuantity = () => {
    const q = quantityFor(data.dose);
    if (q) setData(d => ({ ...d, quantity: { ...(d.quantity || {}), ...q, quantity_manual: false } }));
  };

  // Dilution = quantité de produit pour 100 L de bouillie (dose/ha ÷ volume de bouillie/ha × 100)
  const computeDilution = (dose, waterVolume) => {
    const v = parseFloat(waterVolume);
    if (!dose || dose.value == null || !v) return null;
    const unit = dose.unit || "L/ha";
    if (["L/hL", "kg/hL"].includes(unit)) return { value: dose.value, unit };
    if (unit === "%") return { value: dose.value, unit: "L/hL" };
    const [base, per] = unit.split("/");
    if (per !== "ha") return null;
    const factor = { L: 1, mL: 0.001, kg: 1, g: 0.001 }[base];
    if (factor == null) return null;
    const outUnit = ["kg", "g"].includes(base) ? "kg/hL" : "L/hL";
    return { value: Math.round((dose.value * factor / v) * 100 * 1000) / 1000, unit: outUnit };
  };

  // Complète la dilution calculée quand elle n'a pas été saisie à la main
  const withDilution = (payload, dose = payload.dose,
    manual = payload.recommended_extra_data?.dilution_manual ? payload.recommended_extra_data?.dilution_value : null) => {
    const extra = { ...(payload.recommended_extra_data || {}) };
    if (extra.water_volume_value != null && extra.water_volume_value !== "") extra.water_volume_unit = "L/ha";
    if (manual == null || manual === "") {
      const d = computeDilution(dose, extra.water_volume_value);
      extra.dilution_value = d ? d.value : null;
      extra.dilution_unit = d ? d.unit : null;
      extra.dilution_manual = false;
    } else {
      extra.dilution_value = manual;
      extra.dilution_unit = ["kg/ha", "g/ha", "kg/hL"].includes(dose?.unit) ? "kg/hL" : "L/hL";
      extra.dilution_manual = true;
    }
    return { ...payload, recommended_extra_data: extra };
  };

  const addExtraProduct = (p) => {
    if (!p) return;
    const amm = p.amm_number || p.amm;
    if (amm && (amm === data.product.amm_number || extraProducts.some(x => x.product.amm_number === amm))) {
      toast.info("Ce produit est déjà dans le mélange");
      return;
    }
    let dose = {};
    const m = p.dose_default ? String(p.dose_default).match(/([\d.]+)\s*([a-zA-Z%/]+)/) : null;
    if (m) dose = { value: parseFloat(m[1]), unit: m[2] };
    const dar = p.dar ? parseInt(String(p.dar).replace(/\D/g, ""), 10) : null;
    setExtraProducts(prev => [...prev, {
      product: {
        amm_number: amm,
        commercial_name: p.commercial_name || p.name,
        active_substances: p.substances_actives || p.active_substances || [],
        ephy_verified: true,
        product_type: "PPP",
        category: p.category,
      },
      dose,
      quantity: { ...(quantityFor(dose) || {}), quantity_manual: false },
      dar_days: Number.isNaN(dar) ? null : dar,
    }]);
    setMixCompat(null);
  };

  const updateExtra = (i, patch) => {
    setExtraProducts(prev => prev.map((x, j) => {
      if (j !== i) return x;
      const next = { ...x, ...patch };
      if (patch.quantity) next.quantity = { ...next.quantity, quantity_manual: true }; // saisie à la main
      else if (patch.dose && !x.quantity?.quantity_manual) {
        const q = quantityFor(next.dose);
        next.quantity = { ...x.quantity, ...(q || { product_quantity_value: null }), quantity_manual: false };
      }
      return next;
    }));
  };

  const recalcExtra = (i) => setExtraProducts(prev => prev.map((x, j) => {
    const q = j === i ? quantityFor(x.dose) : null;
    return q ? { ...x, quantity: { ...x.quantity, ...q, quantity_manual: false } } : x;
  }));

  const removeExtra = (i) => {
    setExtraProducts(prev => prev.filter((_, j) => j !== i));
    setMixCompat(null);
  };

  // Une charge utile par produit du passage, reliées par pass_info
  const buildPayloads = () => {
    if (!extraProducts.length) return [withDilution(data)];
    const passId = `pass_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const names = [data.product.commercial_name, ...extraProducts.map(x => x.product.commercial_name)];
    const compat = mixCompat ? { score: mixCompat.score, verdict: mixCompat.verdict } : null;
    const info = (index) => ({ pass_id: passId, index, size: names.length, products: names, compatibility: compat });
    return [
      withDilution({ ...data, pass_info: info(1) }),
      ...extraProducts.map((x, i) => withDilution({
        ...data,
        product: x.product,
        dose: x.dose,
        quantity: x.quantity,
        recommended_extra_data: { ...data.recommended_extra_data, dar_days: x.dar_days ?? null, dilution_value: x.dilution ?? null, dilution_unit: null },
        pass_info: info(i + 2),
      }, x.dose, x.dilution)),
    ];
  };

  const runMixCompat = async () => {
    if (!extraProducts.length) return;
    setMixCompatLoading(true);
    try {
      const all = [data.product, ...extraProducts.map(x => x.product)];
      const res = await authFetch(`${API_BASE}/carnet/compatibility`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          products: all.map(p => ({
            name: p.commercial_name,
            amm: p.amm_number || "",
            matiere_active: (p.active_substances || []).join(", "),
            category: p.category || "",
          })),
          culture: data.crop?.crop_text || cultureType || null,
          parcel_id: parcelId || null,
          ilot_id: ilotId || null,
        }),
      });
      if (res.ok) setMixCompat(await res.json());
      else setMixCompat({ error: true });
    } catch { setMixCompat({ error: true }); }
    setMixCompatLoading(false);
  };

  const set = (path, value) => {
    setData(prev => {
      const clone = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let ref = clone;
      for (let i = 0; i < parts.length - 1; i++) {
        ref[parts[i]] = ref[parts[i]] || {};
        ref = ref[parts[i]];
      }
      ref[parts[parts.length - 1]] = value;
      return clone;
    });
  };

  const runCompliance = async () => {
    const payloads = buildPayloads();
    const results = await Promise.all(payloads.map(p =>
      authFetch(`${API_BASE}/phyto-register/validate-draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(p),
      }).then(r => (r.ok ? r.json() : null)).catch(() => null)
    ));
    const [main, ...others] = results;
    if (!main) return;
    if (!others.length) { setCompliance(main); return; }
    // Mélange : erreurs et alertes de chaque produit, préfixées par son nom
    const prefix = (list, name) => (list || []).map(e => ({ ...e, message: `${name} — ${e.message}` }));
    const merged = { ...main };
    others.forEach((r, i) => {
      if (!r) return;
      const name = extraProducts[i].product.commercial_name;
      merged.blocking_errors = [...(merged.blocking_errors || []), ...prefix(r.blocking_errors, name)];
      merged.strong_warnings = [...(merged.strong_warnings || []), ...prefix(r.strong_warnings, name)];
    });
    merged.is_valid = results.every(r => r && r.is_valid);
    setCompliance(merged);
  };

  useEffect(() => {
    if (step === 3) {
      runCompliance();
      if (extraProducts.length && !mixCompat) runMixCompat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Sprint 4 : auto-fetch météo Open-Meteo à l'étape Application si pas encore renseignée
  useEffect(() => {
    if (step !== 2) return;
    if (data.recommended_extra_data?.weather_temperature_c != null) return;
    const lat = data.location?.gps_lat || parcelRegInfo?.centroid_lat;
    const lon = data.location?.gps_lon || parcelRegInfo?.centroid_lon;
    if (lat == null || lon == null) return;
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/agro/weather?lat=${lat}&lon=${lon}`);
        if (!res.ok) { setWeatherFailed(true); return; }
        const w = await res.json();
        const cur = w.current || {};
        set("recommended_extra_data.weather_temperature_c", cur.temperature != null ? Math.round(cur.temperature) : null);
        set("recommended_extra_data.weather_wind_kmh", cur.wind_speed != null ? Math.round(cur.wind_speed) : null);
        set("recommended_extra_data.weather_humidity_percent", cur.humidity != null ? Math.round(cur.humidity) : null);
        set("recommended_extra_data.weather_source", "Open-Meteo");
        setWeatherFailed(false);
      } catch { setWeatherFailed(true); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Mélange : crée (et éventuellement valide) une entrée par produit du passage
  const saveMix = async (lock) => {
    setSaving(true);
    const failed = [];
    let firstDetails = null;
    for (const p of buildPayloads()) {
      const create = await authFetch(`${API_BASE}/phyto-register/entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      if (!create.ok) { failed.push(p.product.commercial_name); continue; }
      if (!lock) continue;
      const entry = await create.json();
      const val = await authFetch(`${API_BASE}/phyto-register/entries/${entry.entry_id}/validate`, { method: "POST" });
      if (!val.ok) {
        failed.push(p.product.commercial_name);
        const err = await val.json().catch(() => ({}));
        if (!firstDetails && err?.detail?.blocking_errors) firstDetails = err.detail;
      }
    }
    setSaving(false);
    const total = extraProducts.length + 1;
    if (!failed.length) {
      toast.success(lock
        ? `Passage validé : ${total} produits enregistrés et verrouillés dans le registre`
        : `Passage enregistré en brouillon : ${total} produits`);
      onCreated?.();
    } else {
      toast.error(`${failed.length}/${total} produit(s) non ${lock ? "validé(s)" : "enregistré(s)"} : ${failed.join(", ")}. Les autres sont enregistrés — corrigez depuis le registre.`);
      if (firstDetails) setCompliance(firstDetails);
      onCreated?.();
    }
  };

  const saveDraft = async () => {
    if (extraProducts.length && !editEntryId) return saveMix(false);
    setSaving(true);
    const url = editEntryId
      ? `${API_BASE}/phyto-register/entries/${editEntryId}`
      : `${API_BASE}/phyto-register/entries`;
    const method = editEntryId ? "PUT" : "POST";
    const res = await authFetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(withDilution(data)),
    });
    setSaving(false);
    if (res.ok) {
      toast.success(editEntryId ? "Brouillon mis à jour" : "Brouillon enregistré");
      onCreated?.();
    } else {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const submitCorrection = async () => {
    if (!correctionReason.trim() || correctionReason.trim().length < 5) {
      toast.error("Motif de correction requis (5 caractères minimum)");
      return;
    }
    setSaving(true);
    const res = await authFetch(`${API_BASE}/phyto-register/entries/${correctingEntryId}/correct`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ correction_reason: correctionReason.trim(), payload: withDilution(data) }),
    });
    setSaving(false);
    if (res.ok) {
      toast.success("Correction enregistrée — nouvelle version créée");
      onCreated?.();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err?.detail || "Erreur lors de la correction");
    }
  };

  const validateAndLock = async () => {
    if (extraProducts.length && !editEntryId) return saveMix(true);
    setSaving(true);
    // 1. Créer ou mettre à jour le draft
    const url = editEntryId
      ? `${API_BASE}/phyto-register/entries/${editEntryId}`
      : `${API_BASE}/phyto-register/entries`;
    const method = editEntryId ? "PUT" : "POST";
    const create = await authFetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(withDilution(data)),
    });
    if (!create.ok) {
      setSaving(false);
      toast.error(editEntryId ? "Erreur mise à jour brouillon" : "Erreur création brouillon");
      return;
    }
    const entry = await create.json();
    const entryId = editEntryId || entry.entry_id;
    // 2. Valider et verrouiller
    const val = await authFetch(`${API_BASE}/phyto-register/entries/${entryId}/validate`, {
      method: "POST",
    });
    setSaving(false);
    if (val.ok) {
      toast.success("Entrée validée et verrouillée dans le registre");
      onCreated?.();
    } else {
      const err = await val.json().catch(() => ({}));
      toast.error(err?.detail?.message || "Erreurs bloquantes — corrigez avant validation");
      const details = err?.detail;
      if (details?.blocking_errors) setCompliance(details);
    }
  };

  const canNext = () => {
    if (step === 1) return !!data.usage_type;
    return true;
  };

  const isSurface = data.usage_type === "TRAITEMENT_SURFACE" || data.usage_type === "SEMIS_SEMENCES_TRAITEES";
  const isSemences = data.usage_type === "TRAITEMENT_SEMENCES_PLANTS";
  const isSemis = data.usage_type === "SEMIS_SEMENCES_TRAITEES";
  const isClosed = data.usage_type === "ESPACE_FERME";

  // Catalogue E-Phy par catégorie : refData.produits_phyto = dict {herbicide: [...], fongicide: [...], ...}
  const productsByCategory = useMemo(() => {
    const raw = refData?.produits_phyto;
    if (!raw || typeof raw !== "object") return {};
    // Normalise chaque produit : extraire AMM numérique, catégorie, matière active
    const norm = {};
    Object.entries(raw).forEach(([cat, list]) => {
      norm[cat] = (Array.isArray(list) ? list : []).map(p => {
        const ammMatch = String(p.amm || "").match(/(\d{7})/);
        return {
          ...p,
          commercial_name: p.name,
          amm_number: ammMatch ? ammMatch[1] : (p.amm || ""),
          category: cat,
          substances_actives: p.matiere_active ? [p.matiere_active] : [],
        };
      });
    });
    return norm;
  }, [refData]);

  const productsCatalog = productsByCategory[productCategory] || [];
  // Mélange : uniquement à la création d'un traitement (pas en édition, correction ni semences)
  const allowMix = !editEntryId && !correctingEntryId && !isSemis && !isSemences;
  const mixCatalogItems = useMemo(() => Object.values(productsByCategory).flat().map(p => ({
    value: p.amm_number || p.amm,
    label: `${p.commercial_name || p.name} (AMM ${p.amm_number || p.amm})`,
    sublabel: (p.substances_actives || p.active_substances || []).join(", "),
    raw: p,
  })), [productsByCategory]);
  const productCategoryLabels = {
    herbicide: "Herbicide",
    fongicide: "Fongicide",
    insecticide: "Insecticide",
    regulateur: "Régulateur",
    substance_de_base: "Substance de base",
    autre: "Autre",
  };

  // Sélection 1-clic d'un produit fréquent : produit + dose + DAR de la dernière utilisation
  const applyFrequentProduct = (fp) => {
    set("product.amm_number", fp.amm_number);
    set("product.commercial_name", fp.commercial_name);
    set("product.active_substances", fp.active_substances || []);
    set("product.ephy_verified", true);
    set("product.product_type", "PPP");
    set("product.category", fp.category);
    if (fp.category) setProductCategory(fp.category);
    if (fp.last_dose_value != null) {
      set("dose.value", fp.last_dose_value);
      if (fp.last_dose_unit) set("dose.unit", fp.last_dose_unit);
      // La quantité totale (dose × surface traitée) se recalcule automatiquement
      set("quantity.quantity_manual", false);
    }
    if (fp.last_dar_days != null) set("recommended_extra_data.dar_days", fp.last_dar_days);
  };

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[2000] bg-black/50 flex items-center justify-center p-2 sm:p-4" data-testid="registre-wizard">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[95dvh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-emerald-700 to-emerald-800 text-white">
          <div>
            <div className="text-[11px] uppercase tracking-widest opacity-90">
              {correctingEntryId ? "Correction d'une entrée verrouillée" : (editEntryId ? "Modification d'un brouillon" : "Nouvelle intervention")}
            </div>
            <div className="text-base font-bold">Registre phytosanitaire numérique — Étape {step}/3</div>
          </div>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/20" onClick={onClose} data-testid="close-wizard-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Stepper */}
        <div className="px-4 py-2 border-b bg-gray-50 flex items-center gap-1 text-[10px] font-medium">
          {["Produit", "Application", "Contrôle"].map((label, i) => (
            <div key={i} className={`flex-1 text-center py-1 rounded ${step === i + 1 ? "bg-emerald-600 text-white" : (step > i + 1 ? "bg-emerald-100 text-emerald-800" : "bg-white text-gray-400")}`}>
              {i + 1}. {label}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}>
          {step === 1 && (
            <div className="space-y-1.5">
              <p className="text-xs text-gray-500 uppercase font-semibold">Type d'utilisation</p>
              <div className="grid grid-cols-2 gap-1.5">
                {USAGE_TYPES.map(t => (
                  <button key={t.value}
                    onClick={() => set("usage_type", t.value)}
                    data-testid={`usage-type-${t.value}`}
                    className={`text-left p-2 rounded-lg border-2 transition-all flex items-center gap-2 ${data.usage_type === t.value ? "border-emerald-600 bg-emerald-50" : "border-gray-200 hover:border-emerald-300"}`}>
                    <span className="text-lg">{t.icon}</span>
                    <span className="text-[11px] font-medium leading-tight">{t.label}</span>
                    {data.usage_type === t.value && <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <details className="rounded-lg border border-gray-200 bg-gray-50" data-testid="admin-details">
              <summary className="cursor-pointer select-none px-3 py-2 text-[11px] font-medium text-gray-700 flex items-center gap-2">
                {data.holding.siret_holder?.length === 14 && data.location.gps_lat != null ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                ) : (
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                )}
                <span className="flex-1">
                  Infos administratives & localisation <b>pré-remplies</b> — SIRET {data.holding.siret_holder ? `…${data.holding.siret_holder.slice(-4)}` : "manquant"} · {data.location.location_type === "GPS" && data.location.gps_lat != null ? "GPS ✓" : data.location.location_type}
                </span>
                <span className="text-gray-400 underline shrink-0">modifier</span>
              </summary>
              <div className="space-y-3 px-3 pb-3">
              <p className="text-xs text-gray-500 uppercase font-semibold">Détenteur & bénéficiaire</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={data.holding.siret_holder} onChange={e => set("holding.siret_holder", e.target.value.replace(/\D/g, "").slice(0, 14))} placeholder="SIRET détenteur (14 chiffres)" className="h-9 text-sm" data-testid="w-siret-holder" />
                <Input value={data.holding.business_name || ""} onChange={e => set("holding.business_name", e.target.value)} placeholder="Raison sociale" className="h-9 text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!data.beneficiary.is_service_provider_case} onCheckedChange={v => set("beneficiary.is_service_provider_case", v)} data-testid="w-service-provider" />
                <Label className="text-xs">Cas de prestation / entraide</Label>
              </div>
              {data.beneficiary.is_service_provider_case && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input value={data.beneficiary.siret_beneficiary || ""} onChange={e => set("beneficiary.siret_beneficiary", e.target.value.replace(/\D/g, "").slice(0, 14))} placeholder="SIRET bénéficiaire" className="h-9 text-sm" />
                  <Input value={data.beneficiary.beneficiary_name || ""} onChange={e => set("beneficiary.beneficiary_name", e.target.value)} placeholder="Nom bénéficiaire" className="h-9 text-sm" />
                </div>
              )}

              {(isSurface) && (
                <>
                  <div className="border-t pt-2">
                    <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Localisation</p>
                    <Select value={data.location.location_type} onValueChange={v => set("location.location_type", v)}>
                      <SelectTrigger className="h-9 text-sm w-full" data-testid="w-loc-type"><SelectValue placeholder="Type de localisation" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RPG">Parcelle RPG (îlot + parcelle + INSEE)</SelectItem>
                        <SelectItem value="GPS">Coordonnées GPS WGS84</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {data.location.location_type === "RPG" && (
                    <div className="grid grid-cols-3 gap-2">
                      <Input value={data.location.rpg_ilot || ""} onChange={e => set("location.rpg_ilot", e.target.value.slice(0, 4))} placeholder="Îlot (4 car.)" className="h-9 text-sm" data-testid="w-rpg-ilot" />
                      <Input value={data.location.rpg_parcelle || ""} onChange={e => set("location.rpg_parcelle", e.target.value.slice(0, 3))} placeholder="Parcelle (3 car.)" className="h-9 text-sm" data-testid="w-rpg-parcelle" />
                      <Input value={data.location.insee_commune || ""} onChange={e => set("location.insee_commune", e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="INSEE (5 chiffres)" className="h-9 text-sm" data-testid="w-insee" />
                    </div>
                  )}
                  {data.location.location_type === "GPS" && (
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" step="0.000001" value={data.location.gps_lat ?? ""} onChange={e => set("location.gps_lat", e.target.value ? parseFloat(e.target.value) : null)} placeholder="Latitude WGS84" className="h-9 text-sm" data-testid="w-gps-lat" />
                      <Input type="number" step="0.000001" value={data.location.gps_lon ?? ""} onChange={e => set("location.gps_lon", e.target.value ? parseFloat(e.target.value) : null)} placeholder="Longitude WGS84" className="h-9 text-sm" data-testid="w-gps-lon" />
                    </div>
                  )}
                </>
              )}

              </div>
            </details>
          )}

          {step === 1 && isClosed && (
            <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 space-y-2" data-testid="closed-space-block">
              <p className="text-xs font-semibold text-gray-700">🏭 Installation fermée (serre, local…) — obligatoire</p>
              <p className="text-[11px] text-gray-500">Position et surface pré-remplies depuis la parcelle / l'îlot : vérifiez-les. Pas de numéro officiel ? Laissez le numéro vide : un nom qui permet de reconnaître l'installation suffit (ex. « Serre 1 », pré-rempli avec le nom de la parcelle).</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={data.closed_space.installation_number || ""} onChange={e => set("closed_space.installation_number", e.target.value)} placeholder="Numéro officiel (facultatif)" className="h-9 text-sm bg-white" data-testid="w-installation-num" />
                <Input value={data.closed_space.installation_name || ""} onChange={e => set("closed_space.installation_name", e.target.value)} placeholder="Nom de l'installation (ex. Serre 1)" className="h-9 text-sm bg-white" data-testid="w-installation-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" step="0.000001" value={data.closed_space.installation_gps_lat ?? ""} onChange={e => set("closed_space.installation_gps_lat", e.target.value ? parseFloat(e.target.value) : null)} placeholder="Latitude WGS84" className="h-9 text-sm bg-white" data-testid="w-installation-lat" />
                <Input type="number" step="0.000001" value={data.closed_space.installation_gps_lon ?? ""} onChange={e => set("closed_space.installation_gps_lon", e.target.value ? parseFloat(e.target.value) : null)} placeholder="Longitude WGS84" className="h-9 text-sm bg-white" data-testid="w-installation-lon" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" value={data.closed_space.surface_or_volume_value ?? ""} onChange={e => set("closed_space.surface_or_volume_value", e.target.value ? parseFloat(e.target.value) : null)} placeholder="Surface ou volume" className="h-9 text-sm bg-white" data-testid="w-installation-size" />
                <Select value={data.closed_space.surface_or_volume_unit} onValueChange={v => set("closed_space.surface_or_volume_unit", v)}>
                  <SelectTrigger className="h-9 text-sm bg-white"><SelectValue placeholder="Unité" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="m2">m²</SelectItem>
                    <SelectItem value="m3">m³</SelectItem>
                    <SelectItem value="ha">ha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              {!isSemis ? (
                <>
                  {frequentProducts.length > 0 && (
                    <div data-testid="frequent-products">
                      <p className="text-xs text-gray-500 uppercase font-semibold flex items-center gap-1">
                        <Zap className="w-3 h-3 text-amber-500" /> Mes produits fréquents
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {frequentProducts.map(fp => (
                          <button key={fp.amm_number} onClick={() => applyFrequentProduct(fp)}
                            data-testid={`frequent-product-${fp.amm_number}`}
                            className={`text-[11px] px-2.5 py-1.5 rounded-full border transition-all ${data.product.amm_number === fp.amm_number ? "border-emerald-600 bg-emerald-50 text-emerald-800 font-semibold" : "border-gray-200 bg-white text-gray-700 hover:border-emerald-300"}`}>
                            {fp.commercial_name} <span className="opacity-60">×{fp.count}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 uppercase font-semibold">Catégorie d'intervention</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
                    {Object.entries(productCategoryLabels).map(([cat, label]) => {
                      const count = (productsByCategory[cat] || []).length;
                      return (
                        <button
                          key={cat}
                          onClick={() => { setProductCategory(cat); set("product.amm_number", ""); set("product.commercial_name", ""); set("product.ephy_verified", false); }}
                          data-testid={`product-cat-${cat}`}
                          className={`text-[10px] font-medium py-2 rounded transition-all ${productCategory === cat ? "bg-emerald-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-emerald-50"}`}>
                          {label}
                          {count > 0 && <span className="block text-[9px] opacity-75">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mt-3">Produit (E-Phy) — {productCategoryLabels[productCategory]}</p>
                  <SearchableSelect
                    items={productsCatalog.map(p => ({
                      value: p.amm_number || p.amm,
                      label: `${p.commercial_name || p.name} (AMM ${p.amm_number || p.amm})`,
                      sublabel: (p.substances_actives || p.active_substances || []).join(", "),
                      raw: p,
                    }))}
                    value={data.product.amm_number}
                    onSelect={(value, item) => {
                      set("product.amm_number", value);
                      const p = item?.raw;
                      if (p) {
                        set("product.commercial_name", p.commercial_name || p.name);
                        set("product.active_substances", p.substances_actives || p.active_substances || []);
                        set("product.ephy_verified", true);
                        set("product.product_type", "PPP");
                        set("product.category", p.category);
                        // Pré-remplir la dose par défaut si disponible
                        if (p.dose_default && !data.dose.value) {
                          const doseMatch = String(p.dose_default).match(/([\d.]+)\s*([a-zA-Z%/]+)/);
                          if (doseMatch) {
                            const dv = parseFloat(doseMatch[1]);
                            set("dose.value", dv);
                            set("dose.unit", doseMatch[2]);
                            set("quantity.quantity_manual", false); // quantité recalculée automatiquement
                          }
                        }
                        // Pré-remplir DAR si disponible
                        if (p.dar) {
                          const darNum = parseInt(String(p.dar).replace(/\D/g, ""), 10);
                          if (!isNaN(darNum)) set("recommended_extra_data.dar_days", darNum);
                        }
                      }
                    }}
                    placeholder={`Rechercher un ${productCategoryLabels[productCategory].toLowerCase()} par nom ou AMM…`}
                    testId="w-product-select"
                  />
                  {!data.product.ephy_verified && data.product.commercial_name && (
                    <div className="p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                      ⚠️ Produit non vérifié dans E-Phy. Vérifiez l'AMM, l'étiquette et la décision officielle avant validation.
                    </div>
                  )}
                  {data.product.commercial_name && data.product.active_substances?.length > 0 && (
                    <div className="p-2 rounded bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-800">
                      <b>Matière active :</b> {data.product.active_substances.join(", ")}
                    </div>
                  )}
                  <details className="text-[11px]">
                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800">Saisir manuellement (produit hors E-Phy)</summary>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      <Input value={data.product.commercial_name || ""} onChange={e => { set("product.commercial_name", e.target.value); set("product.ephy_verified", false); }} placeholder="Nom commercial du produit" className="h-9 text-sm" data-testid="w-product-name" />
                      <Input value={data.product.amm_number || ""} onChange={e => { set("product.amm_number", e.target.value.replace(/\D/g, "").slice(0, 7)); set("product.ephy_verified", false); }} placeholder="Numéro AMM (7 chiffres)" className="h-9 text-sm" data-testid="w-amm-manual" disabled={!!data.product.is_base_substance} />
                    </div>
                  </details>

                  {/* Substance de base (règlement CE n° 1107/2009 art. 23) */}
                  <div className="mt-2 p-2.5 rounded-lg border" style={{ borderColor: '#8B5CF640', background: '#8B5CF608' }}>
                    <div className="flex items-start gap-2">
                      <Switch
                        checked={!!data.product.is_base_substance}
                        onCheckedChange={v => {
                          set("product.is_base_substance", v);
                          if (v) {
                            // Efface l'AMM (pas nécessaire pour une substance de base)
                            set("product.amm_number", "");
                            set("product.ephy_verified", false);
                          }
                        }}
                        data-testid="w-base-substance"
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <Label className="text-[11px] font-semibold" style={{ color: '#5B21B6' }}>
                          Substance de base — règlement CE n° 1107/2009 (art. 23)
                        </Label>
                        <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>
                          Ex : bicarbonate de sodium, vinaigre, lactosérum, prêle, saccharose, chitosan, hydroxyde de calcium…
                          Ces substances sont approuvées au niveau UE <b>sans AMM française</b>. Le règlement d&apos;exécution CE fait foi.
                        </p>
                        {data.product.is_base_substance && (
                          <Input
                            value={data.product.ce_reference || ""}
                            onChange={e => set("product.ce_reference", e.target.value)}
                            placeholder="Référence CE (ex : Rgt (UE) 2015/762)"
                            className="h-8 text-xs mt-2"
                            data-testid="w-ce-reference"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {allowMix && data.product.commercial_name && (
                    <div className="rounded-lg border border-emerald-300 bg-emerald-50/50 p-3 space-y-2" data-testid="mix-block">
                      <p className="text-xs font-semibold text-gray-700">
                        🧪 Mélange en cuve — même passage ({extraProducts.length + 1}/{MAX_MIX} produits)
                      </p>
                      <div className="flex items-center gap-2 text-[12px]">
                        <span className="w-5 h-5 rounded-full bg-emerald-600 text-white grid place-items-center text-[10px] font-bold shrink-0">1</span>
                        <span className="font-medium truncate">{data.product.commercial_name}</span>
                        {data.product.amm_number && <span className="text-gray-500 shrink-0">AMM {data.product.amm_number}</span>}
                      </div>
                      {extraProducts.map((x, i) => (
                        <div key={`${x.product.amm_number}-${i}`} className="flex items-center gap-2 text-[12px]" data-testid={`mix-product-${i}`}>
                          <span className="w-5 h-5 rounded-full bg-emerald-600 text-white grid place-items-center text-[10px] font-bold shrink-0">{i + 2}</span>
                          <span className="font-medium truncate flex-1">{x.product.commercial_name}</span>
                          {x.product.amm_number && <span className="text-gray-500 shrink-0">AMM {x.product.amm_number}</span>}
                          <button onClick={() => removeExtra(i)} className="text-red-500 hover:text-red-700 p-0.5 shrink-0" aria-label={`Retirer ${x.product.commercial_name} du mélange`} data-testid={`mix-remove-${i}`}>
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {extraProducts.length + 1 < MAX_MIX && (
                        <SearchableSelect
                          items={mixCatalogItems}
                          value=""
                          onSelect={(value, item) => addExtraProduct(item?.raw)}
                          placeholder="+ Ajouter un produit au mélange (toutes catégories)…"
                          testId="mix-add"
                        />
                      )}
                      <p className="text-[10px] text-gray-500">
                        Une ligne de registre sera créée par produit, reliées par le même passage. Les doses se règlent à l'étape suivante ; la compatibilité du mélange est vérifiée avant validation.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-500 uppercase font-semibold">Produit de traitement de semence (semis)</p>
                  <Input value={data.treated_seed_sowing.seed_treatment_product_name || ""} onChange={e => set("treated_seed_sowing.seed_treatment_product_name", e.target.value)} placeholder="Nom du produit de traitement" className="h-9 text-sm" data-testid="w-seed-product" />
                  <Textarea value={(data.treated_seed_sowing.seed_treatment_active_substances || []).join(", ")} onChange={e => set("treated_seed_sowing.seed_treatment_active_substances", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="Substances actives (séparées par virgule)" className="text-sm" rows={2} data-testid="w-seed-actives" />
                  <Input value={data.treated_seed_sowing.lot_number || ""} onChange={e => set("treated_seed_sowing.lot_number", e.target.value)} placeholder="Numéro de lot" className="h-9 text-sm" data-testid="w-seed-lot" />
                </>
              )}

              {isSemences && (
                <div className="border-t pt-2 space-y-2">
                  <p className="text-xs text-gray-500 uppercase font-semibold">Semences traitées</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" value={data.seed_treatment.treated_quantity_value ?? ""} onChange={e => set("seed_treatment.treated_quantity_value", e.target.value ? parseFloat(e.target.value) : null)} placeholder="Quantité traitée" className="h-9 text-sm" />
                    <Select value={data.seed_treatment.treated_quantity_unit} onValueChange={v => set("seed_treatment.treated_quantity_unit", v)}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Unité" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="t">tonne</SelectItem>
                        <SelectItem value="plants">plants</SelectItem>
                        <SelectItem value="graines">graines</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input value={data.seed_treatment.lot_number || ""} onChange={e => set("seed_treatment.lot_number", e.target.value)} placeholder="Numéro de lot" className="h-9 text-sm" />
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 uppercase font-semibold">Application</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] uppercase">Date d'utilisation *</Label>
                  <Input type="date" value={data.timing.use_date || ""} onChange={e => set("timing.use_date", e.target.value)} className="h-9 text-sm" data-testid="w-use-date" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Heure début</Label>
                  <Input type="time" value={data.timing.start_time_local || ""} onChange={e => set("timing.start_time_local", e.target.value)} className="h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Heure fin</Label>
                  <Input type="time" value={data.timing.end_time_local || ""} onChange={e => set("timing.end_time_local", e.target.value)} className="h-9 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                  <Label className="text-[10px] uppercase">Dose *</Label>
                  <Input type="number" step="any" value={data.dose.value ?? ""} onChange={e => set("dose.value", e.target.value ? parseFloat(e.target.value) : null)} className="h-9 text-sm" data-testid="w-dose" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Unité dose</Label>
                  <Select value={data.dose.unit} onValueChange={v => set("dose.unit", v)}>
                    <SelectTrigger className="h-9 text-sm" data-testid="w-dose-unit"><SelectValue placeholder="Unité" /></SelectTrigger>
                    <SelectContent>
                      {["L/ha", "kg/ha", "g/ha", "mL/ha", "%", "L/hL", "kg/hL"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Quantité produit *</Label>
                  <Input type="number" step="any" value={data.quantity.product_quantity_value ?? ""} onChange={e => setData(d => ({ ...d, quantity: { ...d.quantity, product_quantity_value: e.target.value ? parseFloat(e.target.value) : null, quantity_manual: true } }))} className="h-9 text-sm" data-testid="w-qty" />
                  <QuantityHint computed={quantityFor(data.dose)} quantity={data.quantity} manual={!!data.quantity.quantity_manual}
                    needsWater={PER_HL_UNITS.includes(data.dose.unit) && data.dose.value != null} onRecalc={recalcQuantity} testId="w-qty-hint" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Unité quantité</Label>
                  <Select value={data.quantity.product_quantity_unit} onValueChange={v => setData(d => ({ ...d, quantity: { ...d.quantity, product_quantity_unit: v, quantity_manual: true } }))}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Unité" /></SelectTrigger>
                    <SelectContent>{["L", "mL", "kg", "g"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DoseLimitNotice check={doseChecks[0]} crop={cropForDose} />
              {extraProducts.length > 0 && (
                <div className="space-y-2 border-l-2 border-emerald-400 pl-3" data-testid="mix-doses">
                  <p className="text-[10px] uppercase font-semibold text-gray-500">
                    Ci-dessus : {data.product.commercial_name} · Autres produits du mélange
                  </p>
                  {extraProducts.map((x, i) => (
                    <div key={`dose-${i}`} className="space-y-1">
                      <p className="text-[12px] font-semibold text-gray-800">{i + 2}. {x.product.commercial_name}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Input type="number" step="any" value={x.dose.value ?? ""} placeholder="Dose *"
                          onChange={e => updateExtra(i, { dose: { ...x.dose, value: e.target.value ? parseFloat(e.target.value) : null } })}
                          className="h-9 text-sm" data-testid={`mix-dose-${i}`} />
                        <Select value={x.dose.unit} onValueChange={v => updateExtra(i, { dose: { ...x.dose, unit: v } })}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Unité dose" /></SelectTrigger>
                          <SelectContent>{["L/ha", "kg/ha", "g/ha", "mL/ha", "%", "L/hL", "kg/hL"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" step="any" value={x.quantity.product_quantity_value ?? ""} placeholder="Quantité *"
                          onChange={e => updateExtra(i, { quantity: { ...x.quantity, product_quantity_value: e.target.value ? parseFloat(e.target.value) : null } })}
                          className="h-9 text-sm" data-testid={`mix-qty-${i}`} />
                        <Select value={x.quantity.product_quantity_unit} onValueChange={v => updateExtra(i, { quantity: { ...x.quantity, product_quantity_unit: v } })}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Unité" /></SelectTrigger>
                          <SelectContent>{["L", "mL", "kg", "g"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <QuantityHint computed={quantityFor(x.dose)} quantity={x.quantity} manual={!!x.quantity.quantity_manual}
                        needsWater={PER_HL_UNITS.includes(x.dose.unit) && x.dose.value != null} onRecalc={() => recalcExtra(i)} testId={`mix-qty-hint-${i}`} />
                      <DoseLimitNotice check={doseChecks[i + 1]} crop={cropForDose} />
                    </div>
                  ))}
                </div>
              )}
              {(isSurface || isClosed) && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase">Surface/volume traité *</Label>
                    <Input type="number" step="any" value={data.treated_unit.value ?? ""} onChange={e => set("treated_unit.value", e.target.value ? parseFloat(e.target.value) : null)} className="h-9 text-sm" data-testid="w-treated" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase">Unité surface</Label>
                    <Select value={data.treated_unit.unit} onValueChange={v => set("treated_unit.unit", v)}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Unité" /></SelectTrigger>
                      <SelectContent>{["ha", "m2", "m3", "L"].map(u => <SelectItem key={u} value={u}>{u === "m2" ? "m²" : (u === "m3" ? "m³" : u)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {(isSurface || isClosed) && !isSemis && (() => {
                const water = data.recommended_extra_data.water_volume_value;
                const mainManual = !!data.recommended_extra_data.dilution_manual;
                // Une ligne par produit du passage : produit principal puis produits du mélange
                const rows = [
                  { key: "main", name: data.product.commercial_name || "Produit 1", auto: computeDilution(data.dose, water),
                    value: mainManual ? data.recommended_extra_data.dilution_value : null,
                    onChange: (v) => { set("recommended_extra_data.dilution_value", v); set("recommended_extra_data.dilution_manual", v != null); },
                    testId: "w-dilution" },
                  ...extraProducts.map((x, i) => ({
                    key: `x${i}`, name: x.product.commercial_name || `Produit ${i + 2}`, auto: computeDilution(x.dose, water),
                    value: x.dilution ?? null, onChange: (v) => updateExtra(i, { dilution: v }), testId: `mix-dilution-${i}`,
                  })),
                ];
                return (
                  <div className="space-y-2" data-testid="w-spray-block">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase">Volume de bouillie (L/ha)</Label>
                        <Input type="number" step="any" value={water ?? ""}
                          onChange={e => set("recommended_extra_data.water_volume_value", e.target.value ? parseFloat(e.target.value) : null)}
                          placeholder="ex. 300" className="h-9 text-sm" data-testid="w-water-volume" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase">Dilution{rows.length > 1 ? " — par produit du mélange" : ""}</Label>
                      <div className="space-y-1.5 mt-1">
                        {rows.map((r, idx) => (
                          <div key={r.key} className="grid grid-cols-[minmax(0,1fr)_7.5rem] sm:grid-cols-[minmax(0,1fr)_9rem] gap-2 items-center">
                            <div className="min-w-0">
                              <p className="text-[12px] font-medium text-gray-800 truncate">{rows.length > 1 ? `${idx + 1}. ` : ""}{r.name}</p>
                              <p className="text-[10px] text-gray-500">
                                {r.value != null ? "Saisie manuelle — videz la case pour revenir au calcul" :
                                  r.auto ? `Calculée : ${r.auto.value} ${r.auto.unit} (≈ ${r.auto.value} %)` : "Renseignez la dose et le volume de bouillie"}
                              </p>
                            </div>
                            <Input type="number" step="any" value={r.value ?? ""}
                              onChange={e => r.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                              placeholder={r.auto ? `${r.auto.value} ${r.auto.unit}` : "dilution"}
                              title="Calculée depuis la dose et le volume de bouillie ; saisissez une valeur pour la remplacer"
                              className="h-9 text-sm" data-testid={r.testId} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {isSemis && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase">Densité de semis *</Label>
                    <Input type="number" step="any" value={data.treated_seed_sowing.sowing_density_value ?? ""} onChange={e => set("treated_seed_sowing.sowing_density_value", e.target.value ? parseFloat(e.target.value) : null)} className="h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase">Unité densité</Label>
                    <Select value={data.treated_seed_sowing.sowing_density_unit} onValueChange={v => set("treated_seed_sowing.sowing_density_unit", v)}>
                      <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Unité" /></SelectTrigger>
                      <SelectContent>{["kg/ha", "graines/ha", "plants/ha"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={data.crop.crop_text || ""} onChange={e => set("crop.crop_text", e.target.value)} placeholder="Culture ou zone traitée *" className="h-9 text-sm" data-testid="w-crop" />
                <Input value={data.crop.bbch_stage || ""} onChange={e => set("crop.bbch_stage", e.target.value)} placeholder="Stade BBCH (facultatif)" className="h-9 text-sm" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!data.crop.organic_production} onCheckedChange={v => set("crop.organic_production", v)} data-testid="w-organic" />
                <Label className="text-xs">Production biologique</Label>
              </div>

              <div className="border-t pt-2 space-y-2">
                <p className="text-xs text-gray-500 uppercase font-semibold">Recommandations (facultatif)</p>
                {weatherFailed && data.recommended_extra_data.weather_temperature_c == null && (
                  <p className="text-[11px] text-amber-700 flex items-center gap-1" data-testid="weather-fetch-failed">
                    <Info className="w-3 h-3 shrink-0" /> Météo automatique indisponible — vous pouvez saisir température et vent manuellement (facultatif).
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input value={data.recommended_extra_data.target || ""} onChange={e => set("recommended_extra_data.target", e.target.value)} placeholder="Cible (maladie/ravageur/adventice)" className="h-9 text-sm" />
                  <Input value={data.recommended_extra_data.application_method || ""} onChange={e => set("recommended_extra_data.application_method", e.target.value)} placeholder="Mode d'application" className="h-9 text-sm" />
                  <Input value={data.recommended_extra_data.operator_name || ""} onChange={e => set("recommended_extra_data.operator_name", e.target.value)} placeholder="Applicateur" className="h-9 text-sm" />
                  <Input value={data.recommended_extra_data.equipment || ""} onChange={e => set("recommended_extra_data.equipment", e.target.value)} placeholder="Matériel" className="h-9 text-sm" />
                  <Input type="number" value={data.recommended_extra_data.weather_temperature_c ?? ""} onChange={e => set("recommended_extra_data.weather_temperature_c", e.target.value ? parseFloat(e.target.value) : null)} placeholder="Température (°C)" className="h-9 text-sm" />
                  <Input type="number" value={data.recommended_extra_data.weather_wind_kmh ?? ""} onChange={e => set("recommended_extra_data.weather_wind_kmh", e.target.value ? parseFloat(e.target.value) : null)} placeholder="Vent (km/h)" className="h-9 text-sm" />
                  <Input type="number" value={data.recommended_extra_data.dar_days ?? ""} onChange={e => set("recommended_extra_data.dar_days", e.target.value ? parseInt(e.target.value, 10) : null)} placeholder="DAR — Délai avant récolte (jours)" className="h-9 text-sm" data-testid="w-dar" />
                  <Input value={data.worker_reentry.reentry_delay || ""} onChange={e => set("worker_reentry.reentry_delay", e.target.value)} placeholder="Délai de rentrée travailleurs (ex: 6h, 24h)" className="h-9 text-sm" data-testid="w-reentry-delay" />
                </div>
                <Textarea value={data.recommended_extra_data.observations || ""} onChange={e => set("recommended_extra_data.observations", e.target.value)} placeholder="Observations terrain / efficacité" className="text-sm" rows={2} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-[12px] text-blue-900">
                <p className="font-semibold mb-1">Contrôle réglementaire</p>
                <p>Vérification automatique des champs obligatoires de l'arrêté du 24 décembre 2025.</p>
              </div>

              {/* Récapitulatif de l'intervention avant validation */}
              <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 text-[12px] space-y-1" data-testid="wizard-recap">
                <p className="text-[10px] uppercase font-semibold text-gray-500">Récapitulatif</p>
                <p><b>{data.product?.commercial_name || data.treated_seed_sowing?.seed_treatment_product_name || "Produit non renseigné"}</b>{data.product?.amm_number ? ` — AMM ${data.product.amm_number}` : ""}</p>
                <p className="text-gray-600">
                  📅 {data.timing?.use_date || "—"}
                  {data.dose?.value != null ? ` · 💧 ${data.dose.value} ${data.dose.unit || ""}` : ""}
                  {data.quantity?.product_quantity_value != null ? ` · ${data.quantity.product_quantity_value} ${data.quantity.product_quantity_unit || ""} au total` : ""}
                  {data.treated_unit?.value != null ? ` · ${data.treated_unit.value} ${data.treated_unit.unit || ""}` : ""}
                </p>
                {data.crop?.crop_text && <p className="text-gray-600">🌱 {data.crop.crop_text}</p>}
                {(() => {
                  const p = withDilution(data).recommended_extra_data;
                  if (p.water_volume_value == null && p.dilution_value == null) return null;
                  return (
                    <p className="text-gray-600">
                      🚿 {p.water_volume_value != null ? `Bouillie ${p.water_volume_value} L/ha` : "Bouillie non renseignée"}
                      {p.dilution_value != null ? ` · dilution ${p.dilution_value} ${p.dilution_unit || ""}` : ""}
                    </p>
                  );
                })()}
                {extraProducts.map((x, i) => (
                  <p key={`recap-${i}`} className="pt-1 border-t border-gray-200">
                    <b>+ {x.product.commercial_name}</b>{x.product.amm_number ? ` — AMM ${x.product.amm_number}` : ""}
                    <span className="text-gray-600">
                      {x.dose.value != null ? ` · 💧 ${x.dose.value} ${x.dose.unit || ""}` : ""}
                      {x.quantity.product_quantity_value != null ? ` · ${x.quantity.product_quantity_value} ${x.quantity.product_quantity_unit || ""} au total` : ""}
                      {(() => {
                        const d = x.dilution != null
                          ? { value: x.dilution, unit: ["kg/ha", "g/ha", "kg/hL"].includes(x.dose.unit) ? "kg/hL" : "L/hL" }
                          : computeDilution(x.dose, data.recommended_extra_data.water_volume_value);
                        return d ? ` · dilution ${d.value} ${d.unit}` : "";
                      })()}
                    </span>
                  </p>
                ))}
              </div>

              {extraProducts.length > 0 && (
                <div className={`p-3 rounded-lg border ${
                  !mixCompat || mixCompat.error ? "bg-gray-50 border-gray-200"
                    : mixCompat.verdict === "Compatible" ? "bg-emerald-50 border-emerald-300"
                    : mixCompat.verdict === "Partiellement compatible" ? "bg-amber-50 border-amber-300"
                    : "bg-red-50 border-red-300"}`} data-testid="mix-compat">
                  <p className="text-xs font-bold text-gray-800 mb-1">🧪 Compatibilité du mélange ({extraProducts.length + 1} produits)</p>
                  {mixCompatLoading && <p className="text-[11px] text-gray-500">Analyse de compatibilité en cours…</p>}
                  {!mixCompatLoading && mixCompat?.error && (
                    <p className="text-[11px] text-gray-600">
                      Analyse indisponible pour le moment.{" "}
                      <button onClick={runMixCompat} className="underline font-medium">Réessayer</button>
                    </p>
                  )}
                  {!mixCompatLoading && mixCompat && !mixCompat.error && (
                    <div className="space-y-1 text-[11px] text-gray-700">
                      <p><b>{mixCompat.verdict}</b> — score {mixCompat.score}/100</p>
                      {mixCompat.resume && <p>{mixCompat.resume}</p>}
                      {(mixCompat.avertissements || []).filter(Boolean).map((a, i) => <p key={i}>⚠️ {a}</p>)}
                      {["Incompatible", "Dangereux"].includes(mixCompat.verdict) && (
                        <p className="font-semibold text-red-700">Ce mélange est déconseillé : vérifiez l'étiquette et les mentions de mélange avant de valider.</p>
                      )}
                      <p className="text-[10px] text-gray-500 italic">Analyse indicative (IA) — ne remplace pas l'étiquette ni l'avis d'un conseiller.</p>
                    </div>
                  )}
                </div>
              )}

              {!compliance && <p className="text-sm text-gray-500">Analyse en cours…</p>}

              {compliance?.blocking_errors?.length > 0 && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-300" data-testid="compliance-blocking">
                  <p className="text-xs font-bold text-red-900 mb-2 flex items-center gap-1"><XCircle className="w-3 h-3" /> Erreurs bloquantes ({compliance.blocking_errors.length})</p>
                  <ul className="space-y-1">
                    {compliance.blocking_errors.map((e, i) => (
                      <li key={i} className="text-[11px] text-red-800 flex items-start gap-1"><span>•</span> <span><b>[{e.code}]</b> {e.message}</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {compliance?.strong_warnings?.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-300" data-testid="compliance-warnings">
                  <p className="text-xs font-bold text-amber-900 mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Alertes ({compliance.strong_warnings.length})</p>
                  <ul className="space-y-1">
                    {compliance.strong_warnings.map((w, i) => (
                      <li key={i} className="text-[11px] text-amber-800 flex items-start gap-1"><span>•</span> <span>{w.message}</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {compliance?.advice?.length > 0 && (
                <div className="p-3 rounded-lg bg-gray-50 border border-gray-200" data-testid="compliance-advice">
                  <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1"><Info className="w-3 h-3" /> Conseils ({compliance.advice.length})</p>
                  <ul className="space-y-1">
                    {compliance.advice.map((a, i) => (
                      <li key={i} className="text-[11px] text-gray-600 flex items-start gap-1"><span>•</span> <span>{a.message}</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {compliance?.is_valid && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-300">
                  <p className="text-xs font-bold text-emerald-900 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Tous les champs obligatoires sont complets</p>
                </div>
              )}

              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-[11px] text-gray-700">
                <p className="font-semibold">Message légal</p>
                <p>La validation inscrit l'intervention dans le registre phytosanitaire réglementaire. Après validation, la ligne ne pourra plus être modifiée directement. Toute correction fera l'objet d'une nouvelle version historisée.</p>
              </div>

              {correctingEntryId && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-300" data-testid="correction-reason-block">
                  <p className="text-xs font-semibold text-amber-900 mb-1 flex items-center gap-1"><History className="w-3 h-3" /> Motif de correction *</p>
                  <p className="text-[11px] text-amber-800 mb-2">La correction crée une nouvelle version verrouillée. L'entrée d'origine sera conservée avec le statut « Corrigée » pour traçabilité réglementaire.</p>
                  <Textarea
                    value={correctionReason}
                    onChange={(e) => setCorrectionReason(e.target.value)}
                    placeholder="Ex : erreur de dose saisie, mauvais produit sélectionné, date incorrecte…"
                    rows={2}
                    className="text-xs"
                    data-testid="correction-reason-input"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-3 flex flex-wrap items-center justify-between gap-2 bg-gray-50" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <Button variant="ghost" size="sm" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="text-xs">← Précédent</Button>
          <div className="flex flex-wrap justify-end gap-2 ml-auto">
            {step === 3 ? (
              correctingEntryId ? (
                <Button size="sm" onClick={submitCorrection} disabled={saving || !correctionReason.trim() || correctionReason.trim().length < 5} className="text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid="submit-correction-btn">
                  <History className="w-3 h-3 mr-1" />Enregistrer la correction (v+1)
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving} className="text-xs" data-testid="save-draft-btn">Enregistrer en brouillon</Button>
                  <Button size="sm" onClick={validateAndLock} disabled={saving || !compliance?.is_valid} className="text-xs bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="validate-lock-btn">
                    <Lock className="w-3 h-3 mr-1" />Valider dans le registre
                  </Button>
                </>
              )
            ) : (
              <Button size="sm" onClick={() => setStep(step + 1)} disabled={!canNext()} className="text-xs bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="next-step-btn">
                Suivant <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

// ==================== SHARE CONTROLE DAAF/SRAL MODAL ====================
function ShareControlModal({ parcelId, parcelName, onClose }) {
  const [hours, setHours] = useState(24);
  const [scope, setScope] = useState("parcel"); // "parcel" | "exploitation"
  const [controllerNote, setControllerNote] = useState("");
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdShare, setCreatedShare] = useState(null);
  const [shares, setShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(true);

  const fetchShares = useCallback(async () => {
    setLoadingShares(true);
    const res = await authFetch(`${API_BASE}/phyto-register/shares`);
    if (res.ok) setShares(await res.json());
    setLoadingShares(false);
  }, []);
  useEffect(() => { fetchShares(); }, [fetchShares]);

  const shareUrl = createdShare ? `${window.location.origin}/controle/${createdShare.token}` : "";

  const createShare = async () => {
    setCreating(true);
    const res = await authFetch(`${API_BASE}/phyto-register/share`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        parcel_id: scope === "parcel" ? parcelId : null,
        hours: Number(hours) || 24,
        controller_note: controllerNote.trim() || null,
        include_drafts: includeDrafts,
      }),
    });
    setCreating(false);
    if (res.ok) {
      const s = await res.json();
      setCreatedShare(s);
      toast.success("Lien de contrôle généré");
      fetchShares();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err?.detail || "Erreur lors de la création du lien");
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié dans le presse-papier");
    } catch {
      toast.error("Impossible de copier — sélectionnez et copiez manuellement");
    }
  };

  const revokeShare = async (token) => {
    const res = await authFetch(`${API_BASE}/phyto-register/shares/${token}/revoke`, { method: "POST" });
    if (res.ok) {
      toast.success("Lien révoqué");
      if (createdShare?.token === token) setCreatedShare(null);
      fetchShares();
    } else {
      toast.error("Erreur lors de la révocation");
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[2100] bg-black/50 flex items-center justify-center p-2 sm:p-4" data-testid="share-control-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[95dvh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-emerald-800 to-emerald-700 text-white">
          <div>
            <div className="text-[11px] uppercase tracking-widest opacity-90">Sprint 3 · Contrôle DAAF/SRAL</div>
            <div className="text-base font-bold flex items-center gap-2"><Share2 className="w-4 h-4" /> Générer un lien de contrôle lecture seule</div>
          </div>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/20" onClick={onClose} data-testid="close-share-modal">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ WebkitOverflowScrolling: "touch" }}>
          {!createdShare ? (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-[12px] text-blue-900">
                <p className="font-semibold mb-1">À quoi sert ce lien ?</p>
                <p>Il permet à un contrôleur (DAAF, SRAL, coopérative, technicien) d'accéder <b>en lecture seule</b> à votre registre phytosanitaire pendant une durée limitée. Aucun compte n'est nécessaire. L'accès est journalisé (nombre de consultations, date de dernier accès). Vous pouvez révoquer le lien à tout moment.</p>
              </div>

              <div>
                <Label className="text-[11px] uppercase font-semibold text-gray-600">Portée du lien</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button onClick={() => setScope("parcel")} data-testid="scope-parcel"
                    className={`p-2 rounded-lg border-2 text-left text-[12px] ${scope === "parcel" ? "border-emerald-600 bg-emerald-50" : "border-gray-200 hover:border-emerald-300"}`}>
                    <div className="font-semibold">Cette parcelle uniquement</div>
                    <div className="text-[10px] text-gray-500">{parcelName}</div>
                  </button>
                  <button onClick={() => setScope("exploitation")} data-testid="scope-exploitation"
                    className={`p-2 rounded-lg border-2 text-left text-[12px] ${scope === "exploitation" ? "border-emerald-600 bg-emerald-50" : "border-gray-200 hover:border-emerald-300"}`}>
                    <div className="font-semibold">Toute l'exploitation</div>
                    <div className="text-[10px] text-gray-500">Toutes les parcelles au même SIRET</div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] uppercase font-semibold text-gray-600">Durée de validité</Label>
                  <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
                    <SelectTrigger className="h-9 text-sm mt-1" data-testid="share-hours"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 heure</SelectItem>
                      <SelectItem value="6">6 heures</SelectItem>
                      <SelectItem value="24">24 heures (recommandé)</SelectItem>
                      <SelectItem value="72">3 jours</SelectItem>
                      <SelectItem value="168">7 jours (maximum)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase font-semibold text-gray-600">Note (nom du contrôleur, motif)</Label>
                  <Input value={controllerNote} onChange={(e) => setControllerNote(e.target.value)} maxLength={200} placeholder="Ex : M. Dupont — SRAL 974" className="h-9 text-sm mt-1" data-testid="share-note" />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={includeDrafts} onCheckedChange={setIncludeDrafts} data-testid="share-include-drafts" />
                <Label className="text-xs">Inclure aussi les brouillons non validés (déconseillé)</Label>
              </div>

              <div className="pt-2 flex justify-end">
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs h-9" onClick={createShare} disabled={creating} data-testid="create-share-btn">
                  <Share2 className="w-3.5 h-3.5 mr-1" /> {creating ? "Génération…" : "Générer le lien de contrôle"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="share-created">
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-300">
                <p className="text-xs font-bold text-emerald-900 mb-1 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Lien créé — expire le {fmt(createdShare.expires_at)}</p>
                <p className="text-[11px] text-emerald-800">Copiez le lien ci-dessous et transmettez-le au contrôleur par email, SMS ou messagerie. Le lien fonctionne sans compte.</p>
              </div>
              <div className="flex items-center gap-1">
                <Input value={shareUrl} readOnly className="h-9 text-xs font-mono" data-testid="share-url-input" onFocus={(e) => e.target.select()} />
                <Button size="sm" variant="outline" className="h-9 text-xs shrink-0" onClick={() => copyUrl(shareUrl)} data-testid="copy-share-url">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copier
                </Button>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs h-9" onClick={() => setCreatedShare(null)} data-testid="create-another-share">Créer un autre lien</Button>
                <Button size="sm" variant="outline" className="text-xs h-9 border-red-300 text-red-700 hover:bg-red-50" onClick={() => revokeShare(createdShare.token)} data-testid="revoke-created-share">
                  <Ban className="w-3.5 h-3.5 mr-1" /> Révoquer ce lien
                </Button>
              </div>
            </div>
          )}

          {/* Liens existants */}
          <div className="border-t pt-3">
            <p className="text-[11px] uppercase font-semibold text-gray-600 mb-2">Mes liens de contrôle</p>
            {loadingShares ? (
              <p className="text-xs text-gray-400">Chargement…</p>
            ) : shares.length === 0 ? (
              <p className="text-xs text-gray-400">Aucun lien créé pour l'instant.</p>
            ) : (
              <div className="space-y-1.5">
                {shares.map((s) => (
                  <div key={s.token} className={`text-[11px] p-2 rounded border ${s.is_active ? "border-emerald-200 bg-emerald-50/40" : "border-gray-200 bg-gray-50 opacity-70"}`} data-testid={`share-item-${s.token}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono truncate">…{s.token.slice(-10)}</div>
                        <div className="text-gray-500">
                          {s.parcel_id ? `Parcelle ${s.parcel_id}` : "Exploitation entière"} · {s.hours}h · expire {fmt(s.expires_at)}
                        </div>
                        {s.controller_note && <div className="text-gray-500">📝 {s.controller_note}</div>}
                        <div className="text-gray-500">👁️ {s.access_count || 0} accès{s.last_accessed_at ? ` · dernier ${fmt(s.last_accessed_at)}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {s.is_active ? (
                          <>
                            <span className="text-[10px] font-semibold text-emerald-700 uppercase">Actif</span>
                            <Button size="sm" variant="ghost" className="h-7 text-[10px] px-2" onClick={() => copyUrl(`${window.location.origin}/controle/${s.token}`)} data-testid={`copy-existing-${s.token}`}>
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-[10px] px-2 text-red-600" onClick={() => revokeShare(s.token)} data-testid={`revoke-existing-${s.token}`}>
                              <Ban className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <span className="text-[10px] font-semibold text-gray-500 uppercase">{s.revoked ? "Révoqué" : "Expiré"}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

// ==================== MAIN TAB ====================
export default function PhytoRegisterTab({ onOpenItineraries, parcelId, parcelName, refData, surfaceHa, cultureType, ilotId, ilotName, gpsOverride, underGreenhouse, autoOpenWizard, initialFilter }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardPrefill, setWizardPrefill] = useState(null);
  const [editEntryId, setEditEntryId] = useState(null);
  const [correctingEntryId, setCorrectingEntryId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [fieldEvent, setFieldEvent] = useState(null); // { mode, entry } : formulaire plantation / récolte
  const [filterStatus, setFilterStatus] = useState(initialFilter || "all");
  const [parcelRegInfo, setParcelRegInfo] = useState(null);
  const autoOpenedRef = useRef(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const res = await authFetch(`${API_BASE}/phyto-register/entries?parcel_id=${parcelId}${ilotId ? `&ilot_id=${ilotId}` : ""}`);
    if (res.ok) setEntries(await res.json());
    setLoading(false);
  }, [parcelId, ilotId]);

  const fetchParcelInfo = useCallback(async () => {
    const res = await authFetch(`${API_BASE}/phyto-register/parcels/${parcelId}/registry-info`);
    if (res.ok) setParcelRegInfo(await res.json());
  }, [parcelId]);

  useEffect(() => {
    fetchEntries();
    fetchParcelInfo();
  }, [fetchEntries, fetchParcelInfo]);

  // Ouverture auto du wizard (depuis le Dashboard "+ Traitement")
  useEffect(() => {
    if (!autoOpenWizard || autoOpenedRef.current) return;
    if (parcelRegInfo?.siret_holder?.length === 14) {
      autoOpenedRef.current = true;
      setShowWizard(true);
    }
  }, [autoOpenWizard, parcelRegInfo]);

  const filtered = useMemo(() => {
    if (filterStatus === "all") return entries;
    return entries.filter(e => e.status === filterStatus);
  }, [entries, filterStatus]);

  const doExport = async (fmt) => {
    const res = await authFetch(`${API_BASE}/phyto-register/export/${fmt}?parcel_id=${parcelId}`, { method: "POST" });
    if (!res.ok) { toast.error("Erreur export"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `registre_phyto_${parcelId}.${fmt}`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    fetchEntries();
  };

  const canCreate = parcelRegInfo?.siret_holder && parcelRegInfo.siret_holder.length === 14;

  // Dupliquer : copie du passage complet (tous les produits du mélange, bouillie, dilution), datée du jour
  const cleanProduct = (p) => {
    const prod = { ...(p || {}) };
    ["ephy_dataset_version", "ephy_dataset_key", "dose_default_snapshot", "dar_snapshot", "snapshot_captured_at", "ephy_lookup_failed"].forEach(k => delete prod[k]);
    return prod;
  };
  const refillEntry = (clicked) => {
    const passId = clicked.pass_info?.pass_id;
    const group = passId
      ? entries.filter(x => x.pass_info?.pass_id === passId && x.status !== "corrected")
          .sort((a, b) => (a.pass_info?.index || 1) - (b.pass_info?.index || 1))
      : [clicked];
    const e = group[0] || clicked;
    const loc = { ...(e.location || {}) };
    delete loc.geometry_snapshot;
    const extraData = (x) => {
      const r = x.recommended_extra_data || {};
      return {
        target: r.target || "",
        application_method: r.application_method || "",
        operator_name: r.operator_name || "",
        equipment: r.equipment || "",
        dar_days: r.dar_days ?? null,
        water_volume_value: r.water_volume_value ?? null,
        dilution_value: r.dilution_manual ? r.dilution_value : null,
        dilution_manual: !!r.dilution_manual,
      };
    };
    setWizardPrefill({
      usage_type: e.usage_type,
      product: cleanProduct(e.product),
      dose: { ...(e.dose || {}) },
      quantity: { ...(e.quantity || {}) },
      treated_unit: { ...(e.treated_unit || {}) },
      crop: { ...(e.crop || {}) },
      location: loc,
      closed_space: { ...(e.closed_space || {}) },
      seed_treatment: { ...(e.seed_treatment || {}) },
      treated_seed_sowing: { ...(e.treated_seed_sowing || {}) },
      worker_reentry: { ...(e.worker_reentry || { early_reentry: false }) },
      recommended_extra_data: extraData(e),
      timing: {
        timezone: e.timing?.timezone || ((parcelId || "").startsWith("YT-") ? "Indian/Mayotte" : "Indian/Reunion"),
        use_date: new Date().toISOString().slice(0, 10),
      },
      extra_products: group.slice(1).map(x => ({
        product: cleanProduct(x.product),
        dose: { ...(x.dose || {}) },
        quantity: { ...(x.quantity || {}) },
        dar_days: x.recommended_extra_data?.dar_days ?? null,
        dilution: x.recommended_extra_data?.dilution_manual ? x.recommended_extra_data?.dilution_value : null,
      })),
    });
    setShowWizard(true);
    toast.info(group.length > 1
      ? `Passage dupliqué (${group.length} produits) — vérifiez la date et les doses puis validez`
      : "Traitement dupliqué — vérifiez la date et la dose puis validez");
  };

  // Valider un brouillon directement depuis la liste
  const validateDraftEntry = async (entryId) => {
    const res = await authFetch(`${API_BASE}/phyto-register/entries/${entryId}/validate`, { method: "POST" });
    if (res.ok) {
      toast.success("Entrée validée et verrouillée dans le registre");
      fetchEntries();
    } else {
      const err = await res.json().catch(() => ({}));
      const first = err?.detail?.blocking_errors?.[0]?.message;
      toast.error(first ? `Erreur bloquante : ${first}` : (err?.detail?.message || "Erreurs bloquantes — corrigez avant validation"));
    }
  };

  // Charger un brouillon dans le wizard pour édition
  const editDraftEntry = (e) => {
    if (isFieldEvent(e)) { setFieldEvent({ mode: "edit", entry: e }); return; }
    setEditEntryId(e.entry_id);
    setCorrectingEntryId(null);
    setWizardPrefill({
      usage_type: e.usage_type,
      product: { ...(e.product || {}) },
      dose: { ...(e.dose || {}) },
      quantity: { ...(e.quantity || {}) },
      treated_unit: { ...(e.treated_unit || {}) },
      crop: { ...(e.crop || {}) },
      location: { ...(e.location || {}) },
      closed_space: { ...(e.closed_space || {}) },
      seed_treatment: { ...(e.seed_treatment || {}) },
      treated_seed_sowing: { ...(e.treated_seed_sowing || {}) },
      worker_reentry: { ...(e.worker_reentry || { early_reentry: false }) },
      recommended_extra_data: { ...(e.recommended_extra_data || {}) },
      timing: { ...(e.timing || {}) },
      holding: { ...(e.holding || {}) },
      beneficiary: { ...(e.beneficiary || {}) },
    });
    setShowWizard(true);
  };

  // Ouvrir le wizard en mode correction (versioning) pour une entrée verrouillée
  const correctEntry = (e) => {
    if (isFieldEvent(e)) { setFieldEvent({ mode: "correct", entry: e }); return; }
    setCorrectingEntryId(e.entry_id);
    setEditEntryId(null);
    const prod = { ...(e.product || {}) };
    ["ephy_dataset_version", "ephy_dataset_key", "dose_default_snapshot", "dar_snapshot", "snapshot_captured_at", "ephy_lookup_failed"].forEach(k => delete prod[k]);
    setWizardPrefill({
      usage_type: e.usage_type,
      product: prod,
      dose: { ...(e.dose || {}) },
      quantity: { ...(e.quantity || {}) },
      treated_unit: { ...(e.treated_unit || {}) },
      crop: { ...(e.crop || {}) },
      location: { ...(e.location || {}) },
      closed_space: { ...(e.closed_space || {}) },
      seed_treatment: { ...(e.seed_treatment || {}) },
      treated_seed_sowing: { ...(e.treated_seed_sowing || {}) },
      worker_reentry: { ...(e.worker_reentry || { early_reentry: false }) },
      recommended_extra_data: { ...(e.recommended_extra_data || {}) },
      timing: { ...(e.timing || {}) },
      holding: { ...(e.holding || {}) },
      beneficiary: { ...(e.beneficiary || {}) },
    });
    setShowWizard(true);
    toast.info("Correction d'une entrée verrouillée — un motif sera requis à la dernière étape");
  };

  // Supprimer un brouillon (uniquement)
  const deleteDraftEntry = async (entryId) => {
    const res = await authFetch(`${API_BASE}/phyto-register/entries/${entryId}`, { method: "DELETE" });
    if (res.ok) {
      toast.success("Brouillon supprimé");
      setConfirmDelete(null);
      fetchEntries();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err?.detail || "Suppression impossible");
    }
  };

  return (
    <div className="h-full flex flex-col p-3 sm:p-4 overflow-y-auto" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }} data-testid="phyto-register-tab">
      {/* Bandeau légal */}
      <div className="mb-3 p-2 rounded-md bg-blue-50 border border-blue-200 text-[11px] text-blue-900 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <p>Ce module tient un <b>registre phytosanitaire numérique structuré</b>, conçu pour respecter les champs obligatoires de l'arrêté du 24 décembre 2025. Les suggestions IA et alertes sont des aides à la saisie et ne remplacent pas la responsabilité de l'utilisateur, la lecture de l'étiquette produit et de l'AMM.</p>
      </div>

      <ParcelRegistryInfoPanel parcelId={parcelId} onSaved={fetchParcelInfo} />

      {/* Header actions */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="text-sm font-bold flex-1" style={{ color: "#2D6A4F" }}>Registre — {parcelName}</h2>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-8 text-xs w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="draft">Brouillons</SelectItem>
            <SelectItem value="locked">Verrouillées</SelectItem>
            <SelectItem value="exported">Exportées</SelectItem>
            <SelectItem value="corrected">Corrigées</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => doExport("csv")} data-testid="export-csv-btn"><FileDown className="w-3 h-3 mr-1" />CSV</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => doExport("xlsx")} data-testid="export-xlsx-btn"><FileDown className="w-3 h-3 mr-1" />XLSX</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50" onClick={() => setShowShareModal(true)} data-testid="share-control-btn">
          <Share2 className="w-3 h-3 mr-1" />Mode contrôle
        </Button>
        {onOpenItineraries && (
          <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50" onClick={onOpenItineraries} data-testid="open-itineraries-btn">
            <ListOrdered className="w-3 h-3 mr-1" />Itinéraire technique
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-8 text-xs" style={{ borderColor: "#3F7D3A66", color: "#3F7D3A" }} onClick={() => setFieldEvent({ mode: "new" })} disabled={!canCreate} data-testid="new-field-event-btn">
          <Plus className="w-3 h-3 mr-1" />Plantation / Récolte
        </Button>
        <Button size="sm" className="h-8 text-xs bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => setShowWizard(true)} disabled={!canCreate} data-testid="new-entry-btn">
          <Plus className="w-3 h-3 mr-1" />Nouvelle intervention
        </Button>
      </div>

      {!canCreate && (
        <div className="mb-3 p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
          Renseignez d'abord le <b>SIRET détenteur</b> ci-dessus pour pouvoir créer une intervention.
        </div>
      )}

      {/* Entries list */}
      {loading ? (
        <p className="text-sm text-gray-500 py-6 text-center">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-gray-500">
          <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucune intervention enregistrée</p>
          {canCreate && <p className="text-[11px] mt-1">Cliquez sur "Nouvelle intervention" pour démarrer.</p>}
        </div>
      ) : (
        <div className="space-y-2" data-testid="entries-list">
          {filtered.map(e => (
            <div key={e.entry_id} className="p-3 rounded-lg border bg-white hover:shadow-sm transition-shadow" data-testid={`entry-${e.entry_id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={e.status} />
                    {isFieldEvent(e) ? (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase" style={{ background: FIELD_EVENT_TYPES[e.usage_type].bg, color: FIELD_EVENT_TYPES[e.usage_type].color }} data-testid={`field-event-chip-${e.entry_id}`}>
                        {FIELD_EVENT_TYPES[e.usage_type].label}
                      </span>
                    ) : (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase">
                      {USAGE_TYPES.find(t => t.value === e.usage_type)?.label || e.usage_type}
                    </span>
                    )}
                    {e.version > 1 && <span className="text-[10px] text-gray-500">v{e.version}</span>}
                  </div>
                  {isFieldEvent(e) ? (
                  <>
                  <div className="mt-1 text-sm font-medium text-gray-900">
                    {FIELD_EVENT_TYPES[e.usage_type].label} · {e.crop?.variety || "variété ?"}
                  </div>
                  <div className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap mt-1">
                    <span>📅 {e.timing?.use_date || "—"}</span>
                    <span>🌾 {e.crop?.crop_text || "—"}</span>
                    {e.location?.ilot_name && <span className="font-semibold" style={{ color: "#2f6a92" }}>· {e.location.ilot_name}</span>}
                    <span className="font-semibold" style={{ color: FIELD_EVENT_TYPES[e.usage_type].color }}>⚖️ {e.cultivation?.quantity_value != null ? `${e.cultivation.quantity_value} ${e.cultivation.quantity_unit || ""}` : "—"}</span>
                    <span>📐 {e.treated_unit?.value ? `${e.treated_unit.value} ${e.treated_unit.unit || ""}` : "—"}</span>
                  </div>
                  </>
                  ) : (<>
                  <div className="mt-1 text-sm font-medium text-gray-900">
                    {e.product?.commercial_name || e.treated_seed_sowing?.seed_treatment_product_name || "(produit non renseigné)"}
                    {e.product?.amm_number && <span className="text-[11px] text-gray-500 ml-2 font-mono">AMM {e.product.amm_number}</span>}
                  </div>
                  <div className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap mt-1">
                    <span>📅 {e.timing?.use_date || "—"}</span>
                    <span>🌾 {e.crop?.crop_text || "—"}</span>
                    {e.itinerary && e.status === "draft" && (
                      <span className="font-semibold" style={{ color: "#2D6A4F" }} title={`Itinéraire « ${e.itinerary.name} »`}>· Itinéraire {e.itinerary.step}/{e.itinerary.steps}</span>
                    )}
                    {e.duplicated_from && e.status === "draft" && (
                      <span className="font-semibold" style={{ color: "#B7791F" }} title="Copie créée par duplication de cahier : vérifiez puis validez">· Copie à vérifier</span>
                    )}
                    {e.pass_info?.size > 1 && (
                      <span className="font-semibold" style={{ color: "#2D6A4F" }} title={(e.pass_info.products || []).join(" + ")}>
                        · 🧪 Application en mélange {e.pass_info.index}/{e.pass_info.size}
                      </span>
                    )}
                    {e.location?.ilot_name && <span className="font-semibold" style={{ color: "#2f6a92" }}>· {e.location.ilot_name}</span>}
                    <span>💧 {e.dose?.value ? `${e.dose.value} ${e.dose.unit || ""}` : "—"}</span>
                    <span>📐 {e.treated_unit?.value ? `${e.treated_unit.value} ${e.treated_unit.unit || ""}` : "—"}</span>
                  </div>
                  </>)}
                </div>
              </div>
              {e.compliance?.strong_warnings?.length > 0 && (
                <div className="mt-2 text-[10px] text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {e.compliance.strong_warnings.length} alerte(s)
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {e.status === "draft" && (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => validateDraftEntry(e.entry_id)} data-testid={`validate-entry-${e.entry_id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" />Valider dans le registre
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => editDraftEntry(e)} data-testid={`edit-entry-${e.entry_id}`}>
                      <Pencil className="w-3 h-3 mr-1" />Modifier
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px] border-red-300 text-red-700 hover:bg-red-50" onClick={() => setConfirmDelete(e)} data-testid={`delete-entry-${e.entry_id}`}>
                      <Trash2 className="w-3 h-3 mr-1" />Supprimer
                    </Button>
                  </>
                )}
                {(e.status === "locked" || e.status === "validated" || e.status === "exported") && (
                  <Button size="sm" variant="outline" className="h-7 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => correctEntry(e)} data-testid={`correct-entry-${e.entry_id}`}>
                    <History className="w-3 h-3 mr-1" />Corriger (nouvelle version)
                  </Button>
                )}
                {(e.product?.commercial_name || e.treated_seed_sowing?.seed_treatment_product_name) && e.status !== "corrected" && (
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => refillEntry(e)} data-testid={`refill-entry-${e.entry_id}`}>
                    <Copy className="w-3 h-3 mr-1" />Dupliquer
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showWizard && (
        <RegistreWizard
          parcelId={parcelId}
          ilotId={ilotId}
          gpsOverride={gpsOverride}
          underGreenhouse={underGreenhouse}
          installationLabel={[parcelName || parcelId, ilotName].filter(Boolean).join(" — ")}
          parcelRegInfo={parcelRegInfo}
          surfaceHa={surfaceHa}
          cultureType={cultureType}
          refData={refData}
          prefill={wizardPrefill}
          initialStep={wizardPrefill ? (editEntryId || correctingEntryId ? 1 : 2) : 1}
          editEntryId={editEntryId}
          correctingEntryId={correctingEntryId}
          onClose={() => { setShowWizard(false); setWizardPrefill(null); setEditEntryId(null); setCorrectingEntryId(null); }}
          onCreated={() => { setShowWizard(false); setWizardPrefill(null); setEditEntryId(null); setCorrectingEntryId(null); fetchEntries(); }}
        />
      )}

      {fieldEvent && (
        <FieldEventForm
          parcelId={parcelId}
          ilotId={ilotId}
          cultureType={cultureType}
          surfaceHa={surfaceHa}
          entries={entries}
          entry={fieldEvent.entry}
          mode={fieldEvent.mode}
          onClose={() => { setFieldEvent(null); fetchEntries(); }}
          onDone={(r) => { if (!r?.keepOpen) setFieldEvent(null); fetchEntries(); }}
        />
      )}

      {showShareModal && (
        <ShareControlModal
          parcelId={parcelId}
          parcelName={parcelName}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {confirmDelete && (
        <ModalPortal>
        <div className="fixed inset-0 z-[2100] bg-black/50 flex items-center justify-center p-4" data-testid="confirm-delete-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="px-4 py-3 border-b bg-red-50 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-700" />
              <div className="text-sm font-bold text-red-900">Supprimer ce brouillon ?</div>
            </div>
            <div className="p-4 space-y-2 text-[12px] text-gray-700">
              <p><b>{isFieldEvent(confirmDelete) ? `${FIELD_EVENT_TYPES[confirmDelete.usage_type].label} · ${confirmDelete.crop?.variety || ""}` : (confirmDelete.product?.commercial_name || confirmDelete.treated_seed_sowing?.seed_treatment_product_name || "Brouillon")}</b>{confirmDelete.product?.amm_number ? ` — AMM ${confirmDelete.product.amm_number}` : ""}</p>
              <p className="text-gray-500">📅 {confirmDelete.timing?.use_date || "—"} · 🌾 {confirmDelete.crop?.crop_text || "—"}</p>
              <p className="mt-2 text-red-800 bg-red-50 border border-red-200 rounded p-2 text-[11px]">
                Cette action supprime définitivement le brouillon. Elle n'est possible que sur les entrées non validées.
              </p>
            </div>
            <div className="border-t p-3 flex justify-end gap-2 bg-gray-50">
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setConfirmDelete(null)} data-testid="cancel-delete-btn">Annuler</Button>
              <Button size="sm" className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => deleteDraftEntry(confirmDelete.entry_id)} data-testid="confirm-delete-btn">
                <Trash2 className="w-3 h-3 mr-1" />Supprimer définitivement
              </Button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}
